import { Suspense, lazy, useEffect, useRef, useState, type ReactNode } from "react";
import { useTheme } from "@/components/providers/ThemeProvider";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import { useDeferredMount } from "@/hooks/useDeferredMount";
import { getEnhancedMotionPreference } from "@/lib/motionPreference";
import { usePathname } from "@/lib/router";
import { FixedVantaGlobeBackground } from "@/components/visuals/FixedVantaGlobeBackground";

// The animated chrome (header, footer, sticky bar, scroll-to-top, decorative backdrop)
// all import framer-motion / gsap. Loading them lazily keeps those libraries OUT of the
// entry bundle's static graph, so the entry — and the hero it renders — no longer waits
// for framer-motion to download + evaluate. That render-delay was the sole remaining cap
// on the mobile hero LCP. The chrome streams in a beat after first paint; the header
// reserves its height so nothing shifts.
const Header = lazy(() => import("@/components/layout/Header").then((m) => ({ default: m.Header })));
const Footer = lazy(() => import("@/components/layout/Footer").then((m) => ({ default: m.Footer })));
const StickyCalendlyFooter = lazy(() =>
  import("@/components/layout/StickyCalendlyFooter").then((m) => ({ default: m.StickyCalendlyFooter })),
);
const ScrollToTop = lazy(() =>
  import("@/components/common/ScrollToTop").then((m) => ({ default: m.ScrollToTop })),
);
const MotionPathBackdrop = lazy(() =>
  import("@/components/motionpath/MotionPathBackdrop").then((m) => ({ default: m.MotionPathBackdrop })),
);

type AppLayoutProps = {
  children: ReactNode;
  hideGlobalChrome?: boolean;
  showGlobalHeader?: boolean;
};

export function AppLayout({ children, hideGlobalChrome = false, showGlobalHeader = false }: AppLayoutProps) {
  const { theme } = useTheme();
  const reducedMotion = useReducedMotion();
  // Mount the framer-motion-heavy chrome only after the hero has painted (see hook).
  const chromeReady = useDeferredMount();
  const pathname = usePathname();
  const [allowDesktopEffects, setAllowDesktopEffects] = useState(false);
  const [enhancedMotionEnabled, setEnhancedMotionEnabled] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const syncEnhancedMotion = () => {
      setEnhancedMotionEnabled(getEnhancedMotionPreference());
    };

    const update = () => {
      const coarsePointer = window.matchMedia("(pointer: coarse)").matches;
      const saveData =
        "connection" in navigator &&
        Boolean((navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData);
      const deviceMemory =
        "deviceMemory" in navigator
          ? (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? 4
          : 4;

      setAllowDesktopEffects(
        window.innerWidth >= 1280 && !coarsePointer && !saveData && deviceMemory >= 8,
      );
    };

    syncEnhancedMotion();
    update();
    window.addEventListener("resize", update);
    window.addEventListener("storage", syncEnhancedMotion);
    window.addEventListener("choice-enhanced-motion-change", syncEnhancedMotion as EventListener);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("storage", syncEnhancedMotion);
      window.removeEventListener("choice-enhanced-motion-change", syncEnhancedMotion as EventListener);
    };
  }, []);

  const showStickyCalendlyFooter =
    pathname !== "/admin/login" &&
    pathname !== "/blog/create" &&
    pathname !== "/blog/manage" &&
    pathname !== "/blog/preview" &&
    !pathname.startsWith("/blog/edit/") &&
    pathname !== "/setup-call" &&
    pathname !== "/setup-call-minimal";

  const showFooter = !hideGlobalChrome;
  const renderGlobalHeader = !hideGlobalChrome || showGlobalHeader;

  return (
    <div ref={rootRef} className="relative isolate min-h-screen bg-transparent text-foreground" data-app-theme={theme}>
      {hideGlobalChrome ? null : <FixedVantaGlobeBackground theme={theme} />}
      {!hideGlobalChrome && !reducedMotion && allowDesktopEffects && enhancedMotionEnabled ? (
        <Suspense fallback={null}>
          <MotionPathBackdrop />
        </Suspense>
      ) : null}
      {renderGlobalHeader ? (
        chromeReady ? (
          <Suspense fallback={<div className="sticky top-0 z-50 h-14" aria-hidden />}>
            <Header />
          </Suspense>
        ) : (
          // Reserve the header's height until it mounts so nothing shifts (no CLS).
          <div className="sticky top-0 z-50 h-14" aria-hidden />
        )
      ) : null}
      <main
        className={`relative z-10 min-h-screen ${
          !hideGlobalChrome && showStickyCalendlyFooter ? "pb-20 sm:pb-24" : ""
        }`}
      >
        {/* CSS-driven route enter (was framer-motion AnimatePresence). Keyed on pathname
            so the animation replays per route; disabled under reduced-motion. */}
        <div
          key={pathname}
          className={reducedMotion ? undefined : "motion-safe:animate-[route-enter_0.45s_cubic-bezier(0.22,1,0.36,1)]"}
        >
          {children}
        </div>
      </main>
      {showFooter && chromeReady ? (
        <Suspense fallback={null}>
          <Footer className={!hideGlobalChrome && showStickyCalendlyFooter ? "pb-20 sm:pb-24" : undefined} />
        </Suspense>
      ) : null}
      {!hideGlobalChrome && showStickyCalendlyFooter && chromeReady ? (
        <Suspense fallback={null}>
          <StickyCalendlyFooter enhancedMotionEnabled={enhancedMotionEnabled} />
        </Suspense>
      ) : null}
      {!hideGlobalChrome && chromeReady ? (
        <Suspense fallback={null}>
          <ScrollToTop />
        </Suspense>
      ) : null}
    </div>
  );
}
